import { MedicationLotSuggestions } from './MedicationLotSuggestions';
import { activeEvents } from './projection';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { StructuredArrayField } from './StructuredArrayField';
import { useId, useState } from 'react';
import { TZDate } from '@date-fns/tz';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldGroup, FieldLabel, FieldSet, FieldLegend } from '@/components/ui/field';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  latestSchemaVersions,
  eventCatalog,
  eventCatalogV2,
  eventCatalogV3,
  type EventType,
} from './catalog.generated';
import { validatePayload } from './validate';
import type { ProposedEntry } from './PrenatalEntry';
import type { ClinicalEvent, ClinicalSubject } from './types';
export interface EntryField {
  key: string;
  label: string;
  kind?: 'text' | 'number' | 'textarea' | 'boolean' | 'datetime' | 'string-array' | 'json';
  options?: { label: string; value: string | number | boolean }[];
}
export interface EntryDescriptor {
  eventType: string;
  label: string;
  schemaVersion?: number;
  fields?: EntryField[];
  subjectTypes?: ClinicalSubject['subject_type'][];
  initialPayload?: Record<string, unknown>;
}
export function localDateTime(date = new Date(), timeZone?: string): string {
  const d = timeZone ? new TZDate(date, timeZone) : date;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}
export function toOccurredAt(value: string, timeZone?: string): string {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(value)) throw new Error('Choose a valid observation time.');
  const [y, m, d, h, min, sec = 0] = value.split(/\D/).map(Number);
  const date = timeZone ? new TZDate(y, m - 1, d, h, min, sec, 0, timeZone) : new Date(y, m - 1, d, h, min, sec);
  if (
    !Number.isFinite(date.getTime()) ||
    localDateTime(date, timeZone) !== (value.length === 16 ? value + ':00' : value)
  )
    throw new Error('Choose a valid observation time.');
  return new Date(date.getTime()).toISOString();
}
export function ClinicalTimeField({
  value,
  onChange,
  timeZone,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  timeZone?: string;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <Field>
      <FieldLabel htmlFor={id}>Observation time{timeZone ? ` (${timeZone})` : ''}</FieldLabel>
      <Input
        id={id}
        type="datetime-local"
        step="1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="min-h-11"
      />
      <div className="flex flex-wrap gap-2">
        {[0, 5, 10].map((n) => (
          <Button
            key={n}
            type="button"
            variant="outline"
            className="min-h-11"
            disabled={disabled}
            onClick={() => onChange(localDateTime(new Date(Date.now() - n * 60000), timeZone))}>
            {n ? `−${n} minutes` : 'Now'}
          </Button>
        ))}
      </div>
    </Field>
  );
}
type Schema = { type?: string; properties?: Record<string, Schema>; items?: Schema; enum?: readonly unknown[] };
function fieldsFor(schema: Schema, prefix = ''): EntryField[] {
  return Object.entries(schema.properties ?? {}).flatMap(([key, s]) => {
    const path = prefix + key;
    if (s.type === 'object' && s.properties) return fieldsFor(s, path + '.');
    return [
      {
        key: path,
        label: path.replaceAll('_', ' ').replaceAll('.', ' / '),
        kind:
          s.type === 'number' || s.type === 'integer'
            ? 'number'
            : s.type === 'boolean'
              ? 'boolean'
              : s.type === 'array'
                ? s.items?.type === 'string'
                  ? 'string-array'
                  : 'json'
                : s.type === 'object'
                  ? 'json'
                  : key === 'text' || key === 'notes'
                    ? 'textarea'
                    : 'text',
        options: s.enum?.map((value) => ({ value: value as string, label: String(value) })),
      } satisfies EntryField,
    ];
  });
}
export function setPayloadPath(payload: Record<string, unknown>, path: string, value: unknown) {
  const keys = path.split('.');
  let target = payload;
  for (const key of keys.slice(0, -1)) {
    if (!target[key] || typeof target[key] !== 'object') target[key] = {};
    target = target[key] as Record<string, unknown>;
  }
  target[keys.at(-1)!] = value;
}
export function ClinicalEventEntry({
  descriptors,
  subjects,
  onSave,
  disabled = false,
  title = 'Document observations',
  onSaved,
  initialSubjectId,
  timeZone,
  events = [],
  saveLabel = 'Save observations',
}: {
  saveLabel?: string;
  descriptors: EntryDescriptor[];
  subjects: ClinicalSubject[];
  onSave: (entries: ProposedEntry[], occurredAt: string) => Promise<void>;
  disabled?: boolean;
  events?: ClinicalEvent[];
  title?: string;
  onSaved?: () => void;
  initialSubjectId?: string;
  timeZone?: string;
}) {
  const id = useId();
  const [values, setValues] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Record<number, string>>({});
  const [time, setTime] = useState(() => localDateTime(new Date(), timeZone));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const groups = descriptors.map((descriptor, index) => {
    const version = descriptor.schemaVersion ?? latestSchemaVersions[descriptor.eventType as EventType] ?? 1;
    const catalog = version === 3 ? eventCatalogV3 : version === 2 ? eventCatalogV2 : eventCatalog;
    const definition = (catalog as unknown as Record<string, { schema: Schema; subjects: readonly string[] }>)[
      descriptor.eventType
    ];
    const allowed = subjects.filter(
      (s) => s.active !== false && (descriptor.subjectTypes ?? definition?.subjects ?? []).includes(s.subject_type),
    );
    const subjectId =
      selected[index] ??
      (initialSubjectId && allowed.some((s) => s.id === initialSubjectId)
        ? initialSubjectId
        : allowed.length === 1
          ? allowed[0].id
          : '');
    return {
      descriptor,
      index,
      version,
      definition,
      allowed,
      subjectId,
      fields: descriptor.fields ?? fieldsFor(definition?.schema ?? {}),
    };
  });
  async function save() {
    setError('');
    try {
      const entries: ProposedEntry[] = [];
      for (const g of groups) {
        const filled = g.fields.filter((f) => (values[`${g.index}:${f.key}`] ?? '').trim() !== '');
        if (!filled.length) continue;
        if (!g.allowed.some((s) => s.id === g.subjectId)) throw new Error(`${g.descriptor.label}: choose a subject.`);
        const payload = structuredClone(g.descriptor.initialPayload ?? {});
        for (const f of filled) {
          const raw = values[`${g.index}:${f.key}`];
          const option = f.options?.find((o) => String(o.value) === raw);
          const value = option
            ? option.value
            : f.kind === 'number'
              ? Number(raw)
              : f.kind === 'boolean'
                ? raw === 'true'
                : f.kind === 'string-array'
                  ? raw
                      .split('\n')
                      .map((s) => s.trim())
                      .filter(Boolean)
                  : f.kind === 'json'
                    ? JSON.parse(raw)
                    : f.kind === 'datetime'
                      ? toOccurredAt(raw, timeZone)
                      : raw.trim();
          setPayloadPath(payload, f.key, value);
        }
        const errors = validatePayload(g.descriptor.eventType, payload, g.version);
        if (errors.length) throw new Error(`${g.descriptor.label}: ${errors.join('; ')}`);
        entries.push({
          event_type: g.descriptor.eventType as EventType,
          schema_version: g.version,
          subject_id: g.subjectId,
          payload,
        });
      }
      if (!entries.length) throw new Error('Enter at least one observation.');
      const occurredAt = toOccurredAt(time, timeZone);
      setSaving(true);
      await onSave(entries, occurredAt);
      setValues({});
      onSaved?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save observations.');
    } finally {
      setSaving(false);
    }
  }
  return (
    <FieldSet disabled={disabled || saving}>
      <FieldLegend>{title}</FieldLegend>
      <ClinicalTimeField value={time} onChange={setTime} timeZone={timeZone} disabled={disabled || saving} />
      <FieldGroup>
        {groups.map((g) => (
          <FieldSet key={g.index}>
            <FieldLegend>{g.descriptor.label}</FieldLegend>
            {g.allowed.length > 1 && (
              <Field>
                <FieldLabel>Subject</FieldLabel>
                <Select
                  disabled={!!g.subjectId && g.fields.some((f) => (values[`${g.index}:${f.key}`] ?? '').trim() !== '')}
                  value={g.subjectId}
                  onValueChange={(value) => setSelected({ ...selected, [g.index]: value })}>
                  <SelectTrigger className="min-h-11">
                    <SelectValue placeholder="Choose subject" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {g.allowed.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
            )}
            {g.descriptor.eventType === 'MED.ADMIN' && (
              <MedicationLotSuggestions
                drug={values[`${g.index}:drug`] ?? String(g.descriptor.initialPayload?.drug ?? '')}
                onApply={(lot) =>
                  setValues({
                    ...values,
                    [`${g.index}:lot`]: lot.lot,
                    [`${g.index}:expiration`]: lot.expiration ?? '',
                  })
                }
              />
            )}
            <FieldSet disabled={!g.subjectId} className="grid gap-4 sm:grid-cols-2">
              {g.fields.map((f) => {
                const key = `${g.index}:${f.key}`,
                  inputId = `${id}-${key}`,
                  value = values[key] ?? '',
                  change = (v: string) => setValues({ ...values, [key]: v });
                const previous = activeEvents(events)
                  .filter((e) => e.subject_id === g.subjectId && e.event_type === g.descriptor.eventType)
                  .sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at))[0];
                const prior = f.key
                  .split('.')
                  .reduce<unknown>(
                    (v, key) => (v && typeof v === 'object' ? (v as Record<string, unknown>)[key] : undefined),
                    previous?.payload,
                  );
                const placeholder =
                  prior !== undefined
                    ? `Previous: ${typeof prior === 'object' ? JSON.stringify(prior) : String(prior)}`
                    : undefined;
                const arrayProperties = g.definition?.schema.properties?.[f.key]?.items?.properties;
                const options =
                  f.options ??
                  (f.kind === 'boolean'
                    ? [
                        { value: true, label: 'Yes' },
                        { value: false, label: 'No' },
                      ]
                    : undefined);
                return (
                  <Field key={f.key}>
                    <FieldLabel htmlFor={inputId}>{f.label}</FieldLabel>
                    {arrayProperties ? (
                      <StructuredArrayField id={inputId} value={value} onChange={change} properties={arrayProperties} />
                    ) : options && options.length <= 7 ? (
                      <ToggleGroup
                        type="single"
                        variant="outline"
                        value={value}
                        onValueChange={change}
                        className="flex flex-wrap justify-start gap-2"
                        aria-label={f.label}>
                        {options.map((o) => (
                          <ToggleGroupItem
                            key={String(o.value)}
                            value={String(o.value)}
                            className="min-h-11 min-w-11 h-auto whitespace-normal px-3 py-2">
                            {o.label}
                          </ToggleGroupItem>
                        ))}
                      </ToggleGroup>
                    ) : options ? (
                      <Select value={value || '__blank'} onValueChange={(v) => change(v === '__blank' ? '' : v)}>
                        <SelectTrigger id={inputId} className="min-h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="__blank">Not documented</SelectItem>
                            {options.map((o) => (
                              <SelectItem key={String(o.value)} value={String(o.value)}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    ) : ['textarea', 'json', 'string-array'].includes(f.kind ?? '') ? (
                      <Textarea
                        placeholder={placeholder}
                        id={inputId}
                        value={value}
                        onChange={(e) => change(e.target.value)}
                        rows={3}
                      />
                    ) : (
                      <Input
                        id={inputId}
                        placeholder={placeholder}
                        className="min-h-11"
                        type={f.kind === 'number' ? 'number' : f.kind === 'datetime' ? 'datetime-local' : 'text'}
                        step={f.kind === 'number' ? 'any' : undefined}
                        value={value}
                        onChange={(e) => change(e.target.value)}
                      />
                    )}
                  </Field>
                );
              })}
            </FieldSet>
          </FieldSet>
        ))}
      </FieldGroup>
      {error && (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      )}
      <Button type="button" className="min-h-11" onClick={save} disabled={disabled || saving}>
        {saving ? 'Saving locally…' : saveLabel}
      </Button>
    </FieldSet>
  );
}
